
import { PropType, VNode } from 'vue';
import { Wormhole } from 'portal-vue';
import { directive as clickOutside } from 'v-click-outside';
import { WithRefs } from 'vue-typed-refs';
import { nanoid } from 'nanoid';
import invariant from 'invariant';

import { PopperMixin } from '../../mixins';
import { Options as PopperOptions } from '../../mixins/popper';

export type Options = PopperOptions;

export const SIZES = [
  'base',
  'xx-small',
  'x-small',
  'small',
  'medium',
  'large',
] as const;

export type Size = typeof SIZES[number];

type Refs = {
  root: HTMLDivElement;
  dropdown: HTMLDivElement;
};

export default (PopperMixin as WithRefs<Refs, typeof PopperMixin>).extend({
  directives: {
    clickOutside,
  },
  props: {
    uid: {
      type: String,
      default() {
        return nanoid();
      },
    },
    tag: {
      type: String,
      default: 'div',
    },
    size: {
      type: String as PropType<Size>,
      default: 'base' as const,
      validator: (val) => SIZES.includes(val),
    },
    withNubbin: {
      type: Boolean,
      default: false,
    },
    closeOnItemClick: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      isOpen: false,
    };
  },
  computed: {
    activatorAttrs(): Record<string, string | boolean> {
      return { 'aria-haspopup': 'true', title: 'Show More' };
    },
    // eslint-disable-next-line @typescript-eslint/ban-types
    activatorOn(): Record<string, Function> {
      return {
        click: this.toggle,
      };
    },
    portalFrom(): string {
      return `Menu-${this.uid}`;
    },
  },
  watch: {
    isOpen(value: boolean) {
      if (value) {
        Wormhole.open({
          to: 'menu',
          from: this.portalFrom,
          passengers: [this.renderDropdown()],
        });

        this.$nextTick(() => {
          this.createPopperInstance(this.$el, this.$refs.dropdown);
        });
      } else {
        this.destroy();
      }
    },
  },
  beforeDestroy() {
    if (this.isOpen) {
      this.destroy();
    }
  },
  methods: {
    toggle() {
      this.isOpen = !this.isOpen;
    },
    hide() {
      this.isOpen = false;
    },
    clickOutsideMiddleware(e: MouseEvent) {
      const { target } = e;

      if (target) {
        if (this.$el.contains(target as Node)) {
          return false;
        }
      }

      return true;
    },
    onClick() {
      if (this.closeOnItemClick) {
        this.hide();
      }
    },
    renderDropdown(): VNode {
      const h = this.$createElement;
      return h(
        'div',
        {
          ref: 'dropdown',
          key: this.portalFrom,
          attrs: {
            id: this.portalFrom,
          },
          directives: [
            {
              name: 'click-outside',
              value: {
                handler: this.hide,
                middleware: this.clickOutsideMiddleware,
              },
            },
          ],
          on: {
            click: this.onClick,
          },
          class: {
            [`slds-dropdown_${this.size}`]: this.size !== 'base',
            [`slds-nubbin_${this.getNubbin()}`]: this.withNubbin,
          },
          staticClass: 'slds-dropdown bc-dropdown',
        },
        [
          this.$slots.prepend,
          h(
            'ul',
            {
              attrs: { role: 'menu', 'aria-label': 'Show More' },
              staticClass: 'slds-dropdown__list',
            },
            this.$slots.default
          ),
        ]
      );
    },
    destroy() {
      Wormhole.close({
        to: 'menu',
        from: this.portalFrom,
      });
    },
    hasArrow() {
      return this.withNubbin;
    },
  },
  render(h): VNode {
    const { activator } = this.$scopedSlots;
    invariant(activator, "`'activator'` slot is not provided");

    return h(
      this.tag,
      {
        ref: 'root',
        staticClass: 'slds-dropdown-trigger slds-dropdown-trigger_click',
        class: {
          'slds-is-open': this.isOpen,
        },
      },
      [
        activator({
          attrs: this.activatorAttrs,
          on: this.activatorOn,
          isOpen: this.isOpen,
        }),
      ]
    );
  },
});
